// Unified employee incident service (for backward compatibility and convenience)
import { employeeIncidentRegisterService } from './services/employeeIncidentRegister.service';
import { employeeIncidentListService } from './services/employeeIncidentList.service';
import { employeeIncidentGetService } from './services/employeeIncidentGet.service';
import { employeeIncidentExportService } from './services/employeeIncidentExport.service';
import { employeeIncidentListManagementService } from './services/employeeIncidentListManagement.service';
import { employeeIncidentExportManagementService } from './services/employeeIncidentExportManagement.service';
import type { ApiResponse } from '@/api/shared';
import type {
  RegisterIncidentInput,
  RegisterIncidentResult,
} from './types/employeeIncidentRegister.types';
import type { LC_ListIncidentsIn, IncidentListResult } from './types/employeeIncidentList.types';
import type { IncidentDetail } from './types/employeeIncidentGet.types';
import type {
  LC_ListIncidentsManagementIn,
  IncidentManagementPage,
} from './types/employeeIncidentListManagement.types';
import type {
  LC_ExportIncidentsManagementIn,
  IncidentManagementExportHybridResult,
} from './types/employeeIncidentExportManagement.types';

/**
 * Unified EmployeeIncidentService that delegates to modular services
 * Maintains backward compatibility with existing code
 */
export class EmployeeIncidentService {
  // Registration operations
  async register(input: RegisterIncidentInput): Promise<ApiResponse<RegisterIncidentResult>> {
    return employeeIncidentRegisterService.register(input);
  }

  // List operations (self-service, "Mis incidentes")
  async list(params: LC_ListIncidentsIn): Promise<ApiResponse<IncidentListResult>> {
    return employeeIncidentListService.list(params);
  }

  // Detail operations
  async getDetail(employeeId: number, incidentId: number): Promise<ApiResponse<IncidentDetail>> {
    return employeeIncidentGetService.getDetail(employeeId, incidentId);
  }

  // Export operations
  async exportIncidents(
    employeeId: number,
    startDate: string,
    endDate: string,
    errorTypeId?: number | null,
    status?: string | null,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return employeeIncidentExportService.exportIncidents(
      employeeId, startDate, endDate, errorTypeId, status,
    );
  }

  // List management operations (admin-wide, ASIS-5244)
  async listManagement(
    params: LC_ListIncidentsManagementIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<IncidentManagementPage>> {
    return employeeIncidentListManagementService.listManagement(params, signal);
  }

  // Export management operations (admin-wide, hybrid sync/async, ASIS-5245)
  async exportManagement(
    params: LC_ExportIncidentsManagementIn,
  ): Promise<ApiResponse<IncidentManagementExportHybridResult>> {
    return employeeIncidentExportManagementService.exportManagement(params);
  }
}

export const employeeIncidentService = new EmployeeIncidentService();
